import React, { useEffect, useState } from "react";
import { X, Tag, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

/**
 * AffiliatePromoBanner — iter190
 *
 * Generalisation of iter189's Teva-specific banner. Reads /api/affiliate/brands
 * on mount and shows any brand whose promo block is currently live. If multiple
 * brands are live at the same time, we rotate them every 8 s so no partner is
 * hidden. Dismiss lasts 24 h per browser.
 *
 * Click routes through /api/affiliate/redirect?brand={slug}&clickref={code}-banner
 * so AWIN still tracks the click with a distinct campaign tag.
 */
const DISMISS_KEY = "ccu:affiliate-promo-banner:dismissed";
const DISMISS_MS = 24 * 60 * 60 * 1000;
const ROTATE_MS = 8000;

export default function AffiliatePromoBanner() {
  const [promos, setPromos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/affiliate/brands");
        const live = (data?.brands || [])
          .filter((b) => b?.promo?.code)
          .map((b) => ({ slug: b.slug, name: b.name, ...b.promo }));
        if (cancelled) return;
        if (!live.length) return;
        try {
          const raw = localStorage.getItem(DISMISS_KEY);
          if (raw) {
            const ts = parseInt(raw, 10);
            if (Number.isFinite(ts) && Date.now() - ts < DISMISS_MS) return;
          }
        } catch { /* ignore */ }
        setPromos(live);
        setHidden(false);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (promos.length <= 1) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % promos.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [promos.length]);

  if (hidden || !promos.length) return null;
  const p = promos[idx];
  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setHidden(true);
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const dest = p.dest_url || "";
  const href = `${backend}/api/affiliate/redirect?brand=${encodeURIComponent(p.slug)}&dest=${encodeURIComponent(dest)}&clickref=${encodeURIComponent(p.code + "-banner")}`;
  const endLabel = p.valid_to ? new Date(p.valid_to).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;

  return (
    <div className="bg-[#0e0e0e] text-white text-xs sm:text-sm" data-testid="affiliate-promo-banner">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-2 flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 bg-[#ff4500] text-white px-2 py-0.5 text-[10px] mono uppercase tracking-widest font-black shrink-0" data-testid="affiliate-promo-banner-brand">
          <Tag className="w-3 h-3" /> {p.name} · Sale
        </span>
        <a
          href={href}
          target="_blank"
          rel="sponsored nofollow noopener"
          className="flex-1 min-w-0 truncate hover:underline"
          data-testid="affiliate-promo-banner-cta"
        >
          <strong>{p.headline}</strong>
          {p.code && <> — use code <span className="mono font-black text-[#ffb98a]" data-testid="affiliate-promo-banner-code">{p.code}</span></>}
          {endLabel && <> · ends {endLabel}</>}
          <ArrowRight className="w-3.5 h-3.5 inline-block ml-1" />
        </a>
        {promos.length > 1 && (
          <span className="text-white/50 text-[10px] mono shrink-0" aria-hidden="true">
            {idx + 1}/{promos.length}
          </span>
        )}
        <button
          type="button"
          onClick={close}
          className="text-white/60 hover:text-white p-1 shrink-0"
          aria-label="Dismiss"
          data-testid="affiliate-promo-banner-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
